import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IdentityService } from '../../../core/services/IdentityService';
import { AuthService } from './AuthService';
import { LanguageStoreService, LocalStorageService, NavigationService } from '@cinetixx/cinetixx-ui';


@Injectable({
  providedIn: 'root'
})
export class LogAsService {

  private readonly _authService = inject(AuthService);
  private readonly _languageStoreService = inject(LanguageStoreService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _identityService = inject(IdentityService);
  private readonly _localStorageService = inject(LocalStorageService);

  public logAs(token:string): Observable<boolean> {
     const redirectPath = this.getRedirectPathFromQuery();

     return this._authService.authWithToken(token)
       .pipe(
         map(identity => {
           this._localStorageService.clear();
           this._identityService.identity = identity;

           const path = this.getPathWithoutLanguagePrefix(redirectPath ?? '/dashboard');
           const identityLanguage = identity.claims['languageToken'];
           const currentLanguage = this._languageStoreService.language?.token;

           if (identityLanguage && identityLanguage !== currentLanguage) {
             location.href = `/${identityLanguage}${path}`;
           } else {
             this._navigationService.navigateByUrl(path);
           }

           return of(true);
         }),
         catchError(() => {
          this._authService.logout();

          return of(false);
        })
       ) as Observable<boolean>;
  }

  private getRedirectPathFromQuery(): string | null {
    const redirectPath = new URLSearchParams(location.search).get('redirectPath');

    if (!redirectPath) {
      return null;
    }

    return redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
  }

  private getPathWithoutLanguagePrefix(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const segments = normalizedPath.split('/').filter(Boolean);

    if (segments.length > 0 && segments[0].length === 2) {
      return `/${segments.slice(1).join('/')}`;
    }

    return normalizedPath;
  }
}
